import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiFetch } from "@/lib/api-client";
import { withStepUp } from "@/lib/with-step-up";
import { onboardingBatchSchema, onboardingBatchesSchema, onboardingInputRowSchema } from "./onboarding.generated";

export type OnboardingBatch = z.infer<typeof onboardingBatchSchema>;
export const onboardingSearchSchema = z.object({
  batch: z.string().uuid().optional(), cursor: z.string().uuid().optional(),
  q: z.string().max(200).optional(),
  page: z.coerce.number().int().min(1).max(8).optional(),
  filter: z.enum(["all", "ready", "applied", "correction"]).optional(),
});
const base = (tenantID: string) => `/api/v1/tenants/${encodeURIComponent(tenantID)}/users/imports`;
const key = (tenantID: string) => ["people-imports", tenantID] as const;
export const onboardingListOptions = (tenantID: string, cursor?: string) => ({
  queryKey: [...key(tenantID), "list", cursor],
  queryFn: async () => onboardingBatchesSchema.parse(await apiFetch<unknown>(base(tenantID), { searchParams: { cursor } })),
});
export function useOnboardingList(tenantID: string, cursor?: string) { return useQuery(onboardingListOptions(tenantID, cursor)); }
export function useOnboardingBatch(tenantID: string, id?: string) {
  return useQuery({ queryKey: [...key(tenantID), "batch", id], enabled: Boolean(id),
    queryFn: async () => onboardingBatchSchema.parse(await apiFetch<unknown>(`${base(tenantID)}/${id}`)),
  });
}
type PreviewInput = {
  id: string; label: string; group_ids: string[];
  csv?: string; rows?: z.infer<typeof onboardingInputRowSchema>[];
  correction_of?: string; correction_rows?: number[];
};
export function usePreviewOnboarding(tenantID: string) {
  const client = useQueryClient();
  return useMutation({ mutationFn: (input: PreviewInput) => withStepUp(async () =>
    onboardingBatchSchema.parse(await apiFetch<unknown>(base(tenantID), { method: "POST", body: input }))),
    onSuccess: (result) => {
      client.setQueryData([...key(tenantID), "batch", result.id], result);
      void client.invalidateQueries({ queryKey: [...key(tenantID), "list"] });
    },
  });
}
export function useStopOnboarding(tenantID: string) {
  const client = useQueryClient();
  return useMutation({ mutationFn: (review: OnboardingBatch) => withStepUp(async () =>
    onboardingBatchSchema.parse(await apiFetch<unknown>(`${base(tenantID)}/${review.id}/stop`, { method: "POST", body: { digest: review.digest } }))),
    onSuccess: (result) => { client.setQueryData([...key(tenantID), "batch", result.id], result); },
    onSettled: () => { void client.invalidateQueries({ queryKey: key(tenantID) }); },
  });
}
export function useCommitOnboarding(tenantID: string) {
  const client = useQueryClient();
  return useMutation({ mutationFn: async (review: OnboardingBatch) => {
    let result = review;
    for (let pass = 0; pass < 10 && !result.stopped_at && result.rows.some(row => row.state === "pending"); pass++) {
      result = await withStepUp(async () => onboardingBatchSchema.parse(await apiFetch<unknown>(
        `${base(tenantID)}/${review.id}/commit`, { method: "POST", body: { digest: review.digest } })));
      // No credentials are present. Show persisted progress after each bounded response.
      client.setQueryData([...key(tenantID), "batch", review.id], result);
    }
    return result;
  }, onSettled: () => {
    void client.invalidateQueries({ queryKey: key(tenantID) });
    void client.invalidateQueries({ queryKey: ["users"] });
  } });
}
